/**
 * TanStack Query hooks for the SubContractorHub Field Mapping tab (SCH-2.1).
 *
 * - useDiscoverCatalog: read-only catalog + suggestions + existing mappings
 *   (cached 5 min per epic note line 648).
 * - useCreateFieldMapping: single UPSERT mutation.
 * - useBulkCreateFieldMappings: atomic bulk INSERT mutation.
 * - useUpdateFieldMapping: partial-update mutation.
 * - useReorderFieldMappings: drag-precedence mutation. Optimistic update +
 *   rollback on error (AC8).
 * - useDeleteFieldMapping: hard-delete mutation.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/contexts/TenantContext';
import { fieldMappingService } from '@/services/integrations/fieldMappingService';
import type {
  DiscoverCatalogResponse,
  ReorderFieldMappingsRequest,
} from '@/types/integrations/fieldMapping';

const FIVE_MINUTES = 5 * 60 * 1000;

function discoverQueryKey(tenantId: string | undefined) {
  return ['subcontractorhub', tenantId, 'discover-catalog'] as const;
}

export function useDiscoverCatalog() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id;
  return useQuery({
    queryKey: discoverQueryKey(tenantId),
    queryFn: async () => {
      const result = await fieldMappingService.discoverCatalog();
      if (result.error) {
        // Throw so TanStack treats as error state — components handle
        // result.error via isError + error.
        const err = new Error(result.error.message) as Error & {
          code?: string;
          details?: Record<string, unknown>;
        };
        err.code = result.error.code;
        err.details = result.error.details;
        throw err;
      }
      return result.data;
    },
    enabled: !!tenantId,
    staleTime: FIVE_MINUTES,
    // Auto-refetch on window focus is appropriate for this kind of admin
    // tooling — if a teammate edited mappings, this picks it up.
    refetchOnWindowFocus: true,
  });
}

export function useCreateFieldMapping() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = discoverQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: fieldMappingService.createFieldMapping,
    // Splice the server-returned row directly into the cache instead of
    // refetching the full discover-catalog payload (144 fields + suggestions).
    // The endpoint is UPSERT, so existing-key matches are replaced; otherwise
    // appended. Drift on parallel teammate edits is handled by
    // refetchOnWindowFocus on the discover query.
    onSuccess: (result) => {
      if (result.error || !result.data) return;
      const newRow = result.data;
      queryClient.setQueryData<DiscoverCatalogResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        const filtered = prev.existingMappings.filter(
          (m) => m.externalFieldKey !== newRow.externalFieldKey,
        );
        return { ...prev, existingMappings: [...filtered, newRow] };
      });
    },
  });
}

export function useBulkCreateFieldMappings() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = discoverQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: fieldMappingService.bulkCreateFieldMappings,
    onSuccess: (result) => {
      if (result.error || !result.data) return;
      const newRows = result.data.rows;
      const newKeys = new Set(newRows.map((r) => r.externalFieldKey));
      queryClient.setQueryData<DiscoverCatalogResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        const filtered = prev.existingMappings.filter(
          (m) => !newKeys.has(m.externalFieldKey),
        );
        return { ...prev, existingMappings: [...filtered, ...newRows] };
      });
    },
  });
}

export function useUpdateFieldMapping() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = discoverQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof fieldMappingService.updateFieldMapping>[1];
    }) => fieldMappingService.updateFieldMapping(id, body),
    onSuccess: (result) => {
      if (result.error || !result.data) return;
      const updated = result.data;
      queryClient.setQueryData<DiscoverCatalogResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          existingMappings: prev.existingMappings.map((m) =>
            m.id === updated.id ? updated : m,
          ),
        };
      });
    },
  });
}

export function useReorderFieldMappings() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = discoverQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: fieldMappingService.reorderFieldMappings,
    // Optimistic update: apply the new precedence in the cache, then roll
    // back on error (AC8). The user sees the new order immediately; if the
    // API call fails, the FE reverts and a destructive toast shows.
    onMutate: async (req: ReorderFieldMappingsRequest) => {
      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueryData<DiscoverCatalogResponse>(queryKey);
      if (snapshot) {
        const next = { ...snapshot };
        const precedenceById = new Map(req.ordering.map((e) => [e.id, e.precedence]));
        next.existingMappings = snapshot.existingMappings
          .map((row) => {
            const newPrecedence = precedenceById.get(row.id);
            return newPrecedence !== undefined
              ? { ...row, precedence: newPrecedence }
              : row;
          })
          .sort((a, b) => a.precedence - b.precedence);
        queryClient.setQueryData(queryKey, next);
      }
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) {
        queryClient.setQueryData(queryKey, ctx.snapshot);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useDeleteFieldMapping() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const queryKey = discoverQueryKey(currentTenant?.tenant_id);
  return useMutation({
    mutationFn: fieldMappingService.deleteFieldMapping,
    onSuccess: (result, deletedId) => {
      if (result.error) return;
      queryClient.setQueryData<DiscoverCatalogResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          existingMappings: prev.existingMappings.filter((m) => m.id !== deletedId),
        };
      });
    },
  });
}
